import commonsDefaultMessages from '@af/app-builder-studio-commons/i18n/en';
import type { I18NMessages } from '@atlaskit/intl-messages-provider/types';
import chatAssistantDefaultMessages from '@atlassian/app-builder-studio-chat-assistant/i18n/en';
import { createFetchMessagesForLocale } from '@atlassian/atlassian-studio-common';
import messageActionsDefaultMessages from '@atlassian/conversation-assistant-message-actions/i18n/en';

import appBuilderStudioDefaultMessages from './en';

type Catalog = Promise<{ default: I18NMessages }>;

/**
 * The chat panel and its shared message-action renderer ship their own Traduki catalogs.
 * Studio Foundations only aggregates this package, so those messages have to be merged here or
 * they never reach `IntlProvider`.
 */
const mergeCatalogs = async (...catalogs: Catalog[]) => ({
	default: Object.assign(
		{},
		...(await Promise.all(catalogs)).map(({ default: messages }) => messages),
	),
});

const defaultMessages: I18NMessages = {
	...appBuilderStudioDefaultMessages,
	...chatAssistantDefaultMessages,
	...commonsDefaultMessages,
	...messageActionsDefaultMessages,
};

const asyncLanguages = {
	cs: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-cs" */ './cs'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-cs" */ '@atlassian/app-builder-studio-chat-assistant/i18n/cs'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-cs" */ '@af/app-builder-studio-commons/i18n/cs'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-cs" */ '@atlassian/conversation-assistant-message-actions/i18n/cs'
			),
		),
	da: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-da" */ './da'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-da" */ '@atlassian/app-builder-studio-chat-assistant/i18n/da'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-da" */ '@af/app-builder-studio-commons/i18n/da'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-da" */ '@atlassian/conversation-assistant-message-actions/i18n/da'
			),
		),
	de: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-de" */ './de'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-de" */ '@atlassian/app-builder-studio-chat-assistant/i18n/de'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-de" */ '@af/app-builder-studio-commons/i18n/de'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-de" */ '@atlassian/conversation-assistant-message-actions/i18n/de'
			),
		),
	en: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-en" */ './en'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-en" */ '@atlassian/app-builder-studio-chat-assistant/i18n/en'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-en" */ '@af/app-builder-studio-commons/i18n/en'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-en" */ '@atlassian/conversation-assistant-message-actions/i18n/en'
			),
		),
	'en-GB': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-en_GB" */ './en_GB'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-en_GB" */ '@atlassian/app-builder-studio-chat-assistant/i18n/en_GB'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-en_GB" */ '@af/app-builder-studio-commons/i18n/en_GB'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-en_GB" */ '@atlassian/conversation-assistant-message-actions/i18n/en_GB'
			),
		),
	'en-ZZ': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-en_ZZ" */ './en_ZZ'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-en_ZZ" */ '@atlassian/app-builder-studio-chat-assistant/i18n/en_ZZ'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-en_ZZ" */ '@af/app-builder-studio-commons/i18n/en_ZZ'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-en_ZZ" */ '@atlassian/conversation-assistant-message-actions/i18n/en_ZZ'
			),
		),
	es: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-es" */ './es'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-es" */ '@atlassian/app-builder-studio-chat-assistant/i18n/es'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-es" */ '@af/app-builder-studio-commons/i18n/es'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-es" */ '@atlassian/conversation-assistant-message-actions/i18n/es'
			),
		),
	fi: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-fi" */ './fi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-fi" */ '@atlassian/app-builder-studio-chat-assistant/i18n/fi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-fi" */ '@af/app-builder-studio-commons/i18n/fi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-fi" */ '@atlassian/conversation-assistant-message-actions/i18n/fi'
			),
		),
	fr: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-fr" */ './fr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-fr" */ '@atlassian/app-builder-studio-chat-assistant/i18n/fr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-fr" */ '@af/app-builder-studio-commons/i18n/fr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-fr" */ '@atlassian/conversation-assistant-message-actions/i18n/fr'
			),
		),
	hr: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-hr" */ './hr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-hr" */ '@atlassian/app-builder-studio-chat-assistant/i18n/hr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-hr" */ '@af/app-builder-studio-commons/i18n/hr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-hr" */ '@atlassian/conversation-assistant-message-actions/i18n/hr'
			),
		),
	hu: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-hu" */ './hu'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-hu" */ '@atlassian/app-builder-studio-chat-assistant/i18n/hu'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-hu" */ '@af/app-builder-studio-commons/i18n/hu'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-hu" */ '@atlassian/conversation-assistant-message-actions/i18n/hu'
			),
		),
	it: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-it" */ './it'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-it" */ '@atlassian/app-builder-studio-chat-assistant/i18n/it'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-it" */ '@af/app-builder-studio-commons/i18n/it'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-it" */ '@atlassian/conversation-assistant-message-actions/i18n/it'
			),
		),
	ja: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-ja" */ './ja'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-ja" */ '@atlassian/app-builder-studio-chat-assistant/i18n/ja'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-ja" */ '@af/app-builder-studio-commons/i18n/ja'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-ja" */ '@atlassian/conversation-assistant-message-actions/i18n/ja'
			),
		),
	ko: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-ko" */ './ko'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-ko" */ '@atlassian/app-builder-studio-chat-assistant/i18n/ko'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-ko" */ '@af/app-builder-studio-commons/i18n/ko'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-ko" */ '@atlassian/conversation-assistant-message-actions/i18n/ko'
			),
		),
	nb: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-nb" */ './nb'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-nb" */ '@atlassian/app-builder-studio-chat-assistant/i18n/nb'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-nb" */ '@af/app-builder-studio-commons/i18n/nb'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-nb" */ '@atlassian/conversation-assistant-message-actions/i18n/nb'
			),
		),
	nl: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-nl" */ './nl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-nl" */ '@atlassian/app-builder-studio-chat-assistant/i18n/nl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-nl" */ '@af/app-builder-studio-commons/i18n/nl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-nl" */ '@atlassian/conversation-assistant-message-actions/i18n/nl'
			),
		),
	pl: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-pl" */ './pl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-pl" */ '@atlassian/app-builder-studio-chat-assistant/i18n/pl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-pl" */ '@af/app-builder-studio-commons/i18n/pl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-pl" */ '@atlassian/conversation-assistant-message-actions/i18n/pl'
			),
		),
	'pt-BR': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-pt_BR" */ './pt_BR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-pt_BR" */ '@atlassian/app-builder-studio-chat-assistant/i18n/pt_BR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-pt_BR" */ '@af/app-builder-studio-commons/i18n/pt_BR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-pt_BR" */ '@atlassian/conversation-assistant-message-actions/i18n/pt_BR'
			),
		),
	ru: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-ru" */ './ru'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-ru" */ '@atlassian/app-builder-studio-chat-assistant/i18n/ru'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-ru" */ '@af/app-builder-studio-commons/i18n/ru'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-ru" */ '@atlassian/conversation-assistant-message-actions/i18n/ru'
			),
		),
	sl: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-sl" */ './sl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-sl" */ '@atlassian/app-builder-studio-chat-assistant/i18n/sl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-sl" */ '@af/app-builder-studio-commons/i18n/sl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-sl" */ '@atlassian/conversation-assistant-message-actions/i18n/sl'
			),
		),
	'sr-RS': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-sr_RS" */ './sr_RS'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-sr_RS" */ '@atlassian/app-builder-studio-chat-assistant/i18n/sr_RS'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-sr_RS" */ '@af/app-builder-studio-commons/i18n/sr_RS'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-sr_RS" */ '@atlassian/conversation-assistant-message-actions/i18n/sr_RS'
			),
		),
	'sr-YR': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-sr_YR" */ './sr_YR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-sr_YR" */ '@atlassian/app-builder-studio-chat-assistant/i18n/sr_YR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-sr_YR" */ '@af/app-builder-studio-commons/i18n/sr_YR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-sr_YR" */ '@atlassian/conversation-assistant-message-actions/i18n/sr_YR'
			),
		),
	sv: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-sv" */ './sv'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-sv" */ '@atlassian/app-builder-studio-chat-assistant/i18n/sv'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-sv" */ '@af/app-builder-studio-commons/i18n/sv'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-sv" */ '@atlassian/conversation-assistant-message-actions/i18n/sv'
			),
		),
	th: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-th" */ './th'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-th" */ '@atlassian/app-builder-studio-chat-assistant/i18n/th'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-th" */ '@af/app-builder-studio-commons/i18n/th'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-th" */ '@atlassian/conversation-assistant-message-actions/i18n/th'
			),
		),
	tr: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-tr" */ './tr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-tr" */ '@atlassian/app-builder-studio-chat-assistant/i18n/tr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-tr" */ '@af/app-builder-studio-commons/i18n/tr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-tr" */ '@atlassian/conversation-assistant-message-actions/i18n/tr'
			),
		),
	uk: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-uk" */ './uk'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-uk" */ '@atlassian/app-builder-studio-chat-assistant/i18n/uk'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-uk" */ '@af/app-builder-studio-commons/i18n/uk'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-uk" */ '@atlassian/conversation-assistant-message-actions/i18n/uk'
			),
		),
	vi: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-vi" */ './vi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-vi" */ '@atlassian/app-builder-studio-chat-assistant/i18n/vi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-vi" */ '@af/app-builder-studio-commons/i18n/vi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-vi" */ '@atlassian/conversation-assistant-message-actions/i18n/vi'
			),
		),
	zh: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-zh" */ './zh'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-zh" */ '@atlassian/app-builder-studio-chat-assistant/i18n/zh'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-zh" */ '@af/app-builder-studio-commons/i18n/zh'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-zh" */ '@atlassian/conversation-assistant-message-actions/i18n/zh'
			),
		),
	'zh-TW': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-i18n-zh_TW" */ './zh_TW'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/app-builder-studio-chat-assistant-i18n-zh_TW" */ '@atlassian/app-builder-studio-chat-assistant/i18n/zh_TW'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@af/app-builder-studio-commons-i18n-zh_TW" */ '@af/app-builder-studio-commons/i18n/zh_TW'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/conversation-assistant-message-actions-i18n-zh_TW" */ '@atlassian/conversation-assistant-message-actions/i18n/zh_TW'
			),
		),
} as const;

export const fetchMessagesForLocale: (locale: string) => Promise<I18NMessages> =
	createFetchMessagesForLocale(asyncLanguages, defaultMessages);

export { defaultMessages };
